(() => {
  const refreshTimers = new WeakMap();
  const chatTimers = new WeakMap();
  const chatStreams = new WeakMap();
  const countdownTimers = new WeakMap();

  function matchingViewer(documentFragment, current) {
    return [
      ...documentFragment.querySelectorAll("[data-canopy-live-viewer]"),
    ].find((viewer) => viewer.dataset.blockId === current.dataset.blockId);
  }

  function replaceDynamicRegion(current, next, selector) {
    const currentRegion = current.querySelector(selector);
    const nextRegion = next.querySelector(selector);
    if (currentRegion && nextRegion) {
      const currentChat = currentRegion.querySelector(
        "[data-canopy-live-chat]",
      );
      const nextChat = nextRegion.querySelector("[data-canopy-live-chat]");
      if (currentChat && nextChat) nextChat.replaceWith(currentChat);
      currentRegion.replaceWith(nextRegion);
    }
  }

  function stopRefreshing(viewer) {
    const timer = refreshTimers.get(viewer);
    if (timer) window.clearInterval(timer);
    refreshTimers.delete(viewer);
  }

  async function refreshViewer(viewer) {
    if (!viewer.isConnected || document.hidden) return;
    const sectionId = viewer.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set("section_id", sectionId);
    const response = await fetch(url, {
      headers: { Accept: "text/html" },
      cache: "no-store",
    });
    if (!response.ok) return;

    const html = await response.text();
    const rendered = new DOMParser().parseFromString(html, "text/html");
    const nextViewer = matchingViewer(rendered, viewer);
    if (!nextViewer) return;

    const streamChanged =
      viewer.dataset.hasActive !== nextViewer.dataset.hasActive ||
      viewer.dataset.playbackId !== nextViewer.dataset.playbackId;

    if (streamChanged) {
      stopRefreshing(viewer);
      viewer.replaceWith(nextViewer);
      initialize(nextViewer.parentElement || document);
      return;
    }

    replaceDynamicRegion(viewer, nextViewer, "[data-canopy-live-header]");
    replaceDynamicRegion(viewer, nextViewer, "[data-canopy-live-commerce]");
    replaceDynamicRegion(viewer, nextViewer, "[data-canopy-live-controls]");
    initialize(viewer);
  }

  function startRefreshing(viewer) {
    if (refreshTimers.has(viewer)) return;
    const timer = window.setInterval(() => {
      refreshViewer(viewer).catch(() => {});
    }, 5000);
    refreshTimers.set(viewer, timer);
  }

  function chatUrl(chat) {
    const url = new URL(chat.dataset.endpoint, window.location.origin);
    url.searchParams.set("show_handle", chat.dataset.showHandle);
    return url;
  }

  function chatStreamUrl(chat) {
    const url = chatUrl(chat);
    url.pathname = `${url.pathname.replace(/\/$/, "")}/stream`;
    return url;
  }

  function messageElement(message, pinnedMessageId) {
    const item = document.createElement("li");
    item.className = "canopy-live-chat__message";
    if (message.role === "host") item.dataset.role = "host";
    if (message.id === pinnedMessageId) item.dataset.pinned = "true";

    const heading = document.createElement("div");
    const author = document.createElement("strong");
    author.textContent =
      message.id === pinnedMessageId
        ? `Pinned · ${message.authorName}`
        : message.authorName;
    const time = document.createElement("time");
    time.dateTime = message.createdAt;
    time.textContent = new Date(message.createdAt).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
    heading.append(author, time);

    const body = document.createElement("p");
    body.textContent = message.body;
    item.append(heading, body);
    return item;
  }

  function renderChat(chat, state) {
    const status = chat.querySelector(".canopy-live-chat__status");
    const messages = chat.querySelector(".canopy-live-chat__messages");
    const form = chat.querySelector(".canopy-live-chat__form");
    const input = form?.querySelector("input");
    const button = form?.querySelector("button");

    status.textContent = !state.authenticated
      ? "Log in to join the live conversation."
      : state.postingEnabled
        ? `${state.messages.length} message${state.messages.length === 1 ? "" : "s"}`
        : "The host has paused viewer messages.";
    const visibleMessages = state.messages.slice(-7);
    const pinnedMessage = state.messages.find(
      (message) => message.id === state.pinnedMessageId,
    );
    if (
      pinnedMessage &&
      !visibleMessages.some((message) => message.id === pinnedMessage.id)
    ) {
      visibleMessages.unshift(pinnedMessage);
    }
    messages.replaceChildren(
      ...visibleMessages.map((message) =>
        messageElement(message, state.pinnedMessageId),
      ),
    );
    messages.scrollTop = messages.scrollHeight;
    if (input) input.disabled = !state.postingEnabled;
    if (button) button.disabled = !state.postingEnabled;
  }

  async function loadChat(chat) {
    if (!chat.isConnected || document.hidden) return;
    const response = await fetch(chatUrl(chat), {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!response.ok) return;
    renderChat(chat, await response.json());
  }

  async function sendChat(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const chat = form.closest("[data-canopy-live-chat]");
    const input = form.querySelector('input[name="body"]');
    const body = input.value.trim();
    if (!body) return;

    const payload = new FormData();
    payload.set("body", body);
    payload.set("author_name", chat.dataset.authorName || "Viewer");
    const response = await fetch(chatUrl(chat), {
      method: "POST",
      body: payload,
      headers: { Accept: "application/json" },
    });
    const state = await response.json();
    if (!response.ok || state.error) {
      chat.querySelector(".canopy-live-chat__status").textContent =
        state.error || "Could not send message.";
      return;
    }
    input.value = "";
    renderChat(chat, { authenticated: true, ...state });
  }

  function startChat(chat) {
    if (chat.dataset.replay === "true") return;
    if (chatTimers.has(chat)) return;
    const form = chat.querySelector(".canopy-live-chat__form");
    if (form) form.addEventListener("submit", sendChat);
    loadChat(chat).catch(() => {});
    if (chat.dataset.authenticated === "true") {
      const events = new EventSource(chatStreamUrl(chat));
      events.addEventListener("chat", (event) => {
        try {
          renderChat(chat, {
            authenticated: true,
            ...JSON.parse(event.data),
          });
        } catch {
          loadChat(chat).catch(() => {});
        }
      });
      events.onerror = () => loadChat(chat).catch(() => {});
      chatStreams.set(chat, events);
    }
    const timer = window.setInterval(() => {
      if (!chat.isConnected) {
        window.clearInterval(timer);
        chatStreams.get(chat)?.close();
        chatStreams.delete(chat);
        chatTimers.delete(chat);
        return;
      }
      loadChat(chat).catch(() => {});
    }, 10000);
    chatTimers.set(chat, timer);
  }

  function startCountdown(countdown) {
    if (countdownTimers.has(countdown)) return;
    const output = countdown.querySelector("[data-canopy-flash-time]");
    const endsAt = Date.parse(countdown.dataset.endsAt || "");
    const render = () => {
      if (!countdown.isConnected) {
        window.clearInterval(timer);
        countdownTimers.delete(countdown);
        return;
      }
      const seconds = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      const minutes = Math.floor(seconds / 60);
      const remainder = String(seconds % 60).padStart(2, "0");
      output.textContent =
        seconds > 0 ? `${minutes}:${remainder} left` : "Ended";
    };
    const timer = window.setInterval(render, 1000);
    render();
    countdownTimers.set(countdown, timer);
  }

  async function addToCart(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const button = form.querySelector("button");
    if (!button) return;
    const label = button.textContent;
    button.disabled = true;
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Could not add to cart.");
      button.textContent = "Added";
    } catch {
      button.textContent = "Try again";
    }
    window.setTimeout(() => {
      button.textContent = label;
      button.disabled = false;
    }, 1600);
  }

  function bindCartForms(root) {
    root.querySelectorAll("[data-canopy-cart-add]").forEach((form) => {
      if (form.dataset.bound === "true") return;
      form.dataset.bound = "true";
      form.addEventListener("submit", addToCart);
    });
  }

  function initialize(root = document) {
    root.querySelectorAll("[data-canopy-live-viewer]").forEach(startRefreshing);
    root.querySelectorAll("[data-canopy-live-chat]").forEach(startChat);
    root
      .querySelectorAll("[data-canopy-flash-countdown]")
      .forEach(startCountdown);
    bindCartForms(root);
  }

  initialize();
  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    event.target
      .querySelectorAll("[data-canopy-live-viewer]")
      .forEach(stopRefreshing);
  });
})();
